
import Vue from 'vue'
import ErrorAlert from '@/components/alert/ErrorAlert.vue'
import { registerApi, RegisterForm } from '@/api/auth/register'
import { isRequiredGenerator, rulePassword, ruleRequired } from '@/utils/rules'
import { AddressProposition } from '@/api/address'
import { civilites, Phone } from '@/types'
import isEmailValid from '@/utils/email'
import { cloneDeep } from 'lodash'
import countries, { getCountryLabel } from '@/utils/countries'
import SwitchLocale from '@/components/SwitchLocale.vue'
import snackbarStore from '@/store/snackbarStore'
import { handleAxiosException } from '@/api'
import { UserType } from '@/utils/userTypes'
import AddressPropositions from '@/components/shared/AddressPropositions.vue'

const defaultForm: RegisterForm = {
  type: 'MAN',
  civilite: undefined,
  businessReason: '',
  firstName: '',
  lastName: '',
  contactName: '',
  address: '',
  complementAddress: '',
  zip: '',
  town: '',
  country: 'FR',
  phone: '',
  email: '',
  password: '',
  paymentZone: 'IBAN',
}

export default Vue.extend({
  components: {
    AddressPropositions,
    ErrorAlert,
    SwitchLocale,
  },
  data() {
    return {
      form: cloneDeep(defaultForm),
      step: 1,
      loading: false,
      showPassword: false,
      showConfirm: false,
      confirmPassword: '',
      errorMessage: '',
      telInput: {} as Phone,
      countryList: countries,
    }
  },
  methods: {
    getCountryLabel,
    isEmailValid,
    isRequiredGenerator,
    capitalizeWords(value: string): string {
      if (!value) {
        return ''
      }

      return value
        .toLowerCase()
        .split(' ')
        .map(segment => segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('-'))
        .join(' ')
    },
    formatName() {
      if (this.form.type === 'MAN') {
        this.form.lastName = this.form.lastName.toUpperCase()
      } else if (this.form.type === 'COMPANY') {
        this.form.lastName = this.capitalizeCompanyName(this.form.lastName)
      }
    },
    capitalizeCompanyName(value: string): string {
      if (!value) {
        return ''
      }

      const forbiddenWords = ['le', 'la', 'les', 'l\'', 'un', 'une', 'de', 'du', 'des', 'd\'', 'au', 'aux', 'en', 'et']

      return value
        .toLowerCase()
        .split(' ')
        .map((word, index) => {
          const isFirstWord = index === 0

          if (word.includes("'")) {
            const [beforeApostrophe, afterApostrophe] = word.split("'")
            if (forbiddenWords.includes(`${beforeApostrophe}'`)) {
              return `${beforeApostrophe}'${afterApostrophe.charAt(0).toUpperCase()}${afterApostrophe.slice(1)}`
            }
          }

          if (!isFirstWord && forbiddenWords.includes(word)) {
            return word
          }

          return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
    },
    handlePhoneNumberInput(item: Phone, number: string, object: { valid: boolean, number: { e164?: string, input: string } }) {
      // eslint-disable-next-line no-param-reassign
      item.valid = object.valid
      // eslint-disable-next-line no-param-reassign
      item.number = object.number.e164 || object.number.input
    },
    selectAccountType(type: UserType) {
      this.form.type = type
      this.step = 2
    },
    isPhoneValid(phone: Phone) {
      return phone.valid || (phone.valid === undefined && phone.number?.length > 0)
    },
    getFullAddress(form: RegisterForm) {
      return `${form.address || ''} ${form.zip || ''} ${form.town || ''}`.replace('  ', ' ')
    },
    selectAddress(proposition: AddressProposition, form: RegisterForm) {
      // eslint-disable-next-line no-param-reassign
      form.address = proposition.properties.name
      // eslint-disable-next-line no-param-reassign
      form.zip = proposition.properties.postcode
      // eslint-disable-next-line no-param-reassign
      form.town = proposition.properties.city
    },
    async register() {
      this.errorMessage = ''
      if ((this.$refs.form as HTMLFormElement).validate()) {
        this.loading = true
        try {
          const mail = this.form.email
          await registerApi.register({ ...this.form, phone: this.telInput.number })
          this.form = cloneDeep(defaultForm)
          this.confirmPassword = ''
          this.telInput = {} as Phone
          snackbarStore.setSnackbar({ text: this.$t('form.signup.success') as string, color: 'success' })
          this.$router.push({ name: 'trigger-activation', params: { email: mail } })
        } catch (error) {
          this.errorMessage = handleAxiosException(error)
        }
        this.loading = false
      }
    },
    showMessageError(message: string) {
      this.errorMessage = message
    },
    toggleShowConfirm() {
      this.showConfirm = !this.showConfirm
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword
    },
    isTrot(): boolean {
      return !!process.env.VUE_APP_IS_TROT
    },
  },
  computed: {
    civilites() {
      return civilites.map(el => ({ value: el, text: (this.$t(`civilite.${el}`)) }))
    },
    rulePassword() {
      return [ruleRequired].concat(rulePassword)
    },
    confirmPasswordError(): string|undefined {
      if (this.form.password === this.confirmPassword) {
        return undefined
      } else {
        return this.$t('form.confirm_password.required') as string
      }
    },
  },

})
